//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';
import { injectIntl } from 'react-intl';

import {
    shareViaPinterest,
    shareViaFacebook,
    shareViaEmail,
    mailMessages,
    getSharePath,
} from '../../helpers';

import Facebook from 'dibs-icons/exports/Facebook';
import Pinterest from 'dibs-icons/exports/Pinterest';
import Envelope from 'dibs-icons/exports/Envelope';

import styles from './SocialIconOverlay.scss';

import type { IntlShape } from 'react-intl';
import type { SocialIconOverlay_photo as TradePhoto } from './__generated__/SocialIconOverlay_photo.graphql';
import type { SocialIconOverlay_photoQuery as TradePhotosQueryOutput } from './__generated__/SocialIconOverlay_photoQuery.graphql';

type SocialIconOverlayProps = {|
    intl: IntlShape,
    firmName: string,
    projectName: string,
    projectUrl: string,
|};

export const onShareEmail = ({
    intl,
    projectUrl,
    projectName,
    firmName,
}: SocialIconOverlayProps) => () => {
    const sharePath = getSharePath(projectUrl);
    const body = intl.formatMessage(mailMessages.body, {
        firmName,
        projectName,
        projectUrl: sharePath,
    });
    const subject = intl.formatMessage(mailMessages.subject, {});
    shareViaEmail({
        body,
        subject,
        url: sharePath,
    });
};

type SocialIconOverlayComponentProps = {
    ...SocialIconOverlayProps,
    photoPath: string,
};

export const SocialIconOverlayComponent = injectIntl(
    ({ intl, firmName, projectName, projectUrl, photoPath }: SocialIconOverlayComponentProps) => {
        firmName = firmName.trim();
        projectName = projectName.trim();
        return (
            <div className={styles.wrapper}>
                <div
                    className={styles.iconWrapper}
                    onClick={onShareEmail({ intl, firmName, projectName, projectUrl })}
                >
                    <Envelope className={styles.icon} />
                </div>
                <div className={styles.iconWrapper} onClick={() => shareViaFacebook(photoPath)}>
                    <Facebook className={styles.icon} />
                </div>
                <div
                    className={styles.iconWrapper}
                    onClick={() =>
                        shareViaPinterest({
                            url: projectUrl,
                            firmName,
                            projectName,
                            photoPath,
                            intl,
                        })
                    }
                >
                    <Pinterest className={styles.icon} />
                </div>
            </div>
        );
    }
);

export const SocialIconOverlay = createFragmentContainer(
    function SocialIconOverlay(props: { photoQuery: TradePhotosQueryOutput, photo: TradePhoto }) {
        const photoQueryGetter = getter(props).at('photoQuery');
        return (
            <SocialIconOverlayComponent
                firmName={photoQueryGetter.value('firmName') || ''}
                projectName={photoQueryGetter.value('projectName') || ''}
                projectUrl={photoQueryGetter.value('projectUrl') || ''}
                photoPath={
                    getter(props)
                        .at('photo')
                        .value('path') || ''
                }
            />
        );
    },
    {
        photoQuery: graphql`
            fragment SocialIconOverlay_photoQuery on TradePhotosQueryOutput {
                firmName
                projectName
                projectUrl
            }
        `,
        photo: graphql`
            fragment SocialIconOverlay_photo on TradePhoto {
                path
            }
        `,
    }
);
